import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Switch } from 'antd';
import { ApiItem } from '../types/api';
import apiConfigurationService, { ApiConfigurationDTO } from '../services/apiConfigurations';
import { message } from '../utils/message';

const { TextArea } = Input;
const { Option } = Select;

interface ApiConfigurationModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingApi?: ApiItem | null;
}

const CATEGORY_OPTIONS = [
  { value: 'weather', label: '날씨' },
  { value: 'search', label: '검색' },
  { value: 'map', label: '지도' },
  { value: 'performance', label: '공연' },
  { value: 'exhibition', label: '전시' },
  { value: 'news', label: '뉴스' },
  { value: 'book', label: '도서' },
  { value: 'culture', label: '문화' },
  { value: 'popup', label: '팝업' },
  { value: 'landscape', label: '경관' },
  { value: 'other', label: '기타' },
];

const ApiConfigurationModal: React.FC<ApiConfigurationModalProps> = ({
  visible,
  onClose,
  onSuccess,
  editingApi,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && editingApi) {
      // 수정 모드: 기존 데이터로 폼 채우기
      form.setFieldsValue({
        name: editingApi.title,
        displayName: editingApi.title,
        description: editingApi.description,
        endpointUrl: editingApi.baseUrl,
        homepage: editingApi.homepage,
        category: editingApi.category,
        isActive: editingApi.isHealthy,
      });
    } else if (visible) {
      // 추가 모드: 폼 초기화
      form.resetFields();
      form.setFieldsValue({
        isActive: true,
        category: 'other',
      });
    }
  }, [visible, editingApi, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const apiData: ApiConfigurationDTO = {
        name: values.name,
        displayName: values.displayName,
        description: values.description,
        category: values.category,
        endpointUrl: values.endpointUrl,
        apiKey: values.apiKey,
        isActive: values.isActive,
        configJson: values.homepage ? { homepage: values.homepage } : undefined,
      };

      if (editingApi) {
        // 수정
        await apiConfigurationService.updateApiConfiguration(editingApi.id, apiData);
        message.success('성공적으로 저장되었습니다.');
      } else {
        // 추가
        await apiConfigurationService.createApiConfiguration(apiData);
        message.success('성공적으로 추가되었습니다.');
      }

      form.resetFields();
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('API 설정 저장 실패:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.errorFields) {
        message.error('입력 항목을 확인해주세요.');
      } else {
        message.error('API 설정 저장 중 오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setLoading(false);
    onClose();
  };

  return (
    <Modal
      title={editingApi ? 'API 설정 수정' : 'API 설정 추가'}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText={editingApi ? '수정' : '추가'}
      cancelText="취소"
      width={600}
      destroyOnHidden={true}
      maskClosable={false}
      keyboard={true}
      styles={{
        body: { maxHeight: '70vh', overflowY: 'auto' },
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)'
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          isActive: true,
          category: 'other',
        }}
      >
        <Form.Item
          label="API 이름 (고유 식별자)"
          name="name"
          rules={[
            { required: true, message: 'API 이름을 입력해주세요.' },
            { max: 100, message: '최대 100자까지 입력 가능합니다.' },
          ]}
        >
          <Input placeholder="예: KMA_WEATHER" disabled={!!editingApi} />
        </Form.Item>

        <Form.Item
          label="표시 이름"
          name="displayName"
          rules={[
            { required: true, message: '표시 이름을 입력해주세요.' },
            { max: 100, message: '최대 100자까지 입력 가능합니다.' },
          ]}
        >
          <Input placeholder="예: 기상청 (KMA) API" />
        </Form.Item>

        <Form.Item
          label="설명 (목적)"
          name="description"
          rules={[{ max: 500, message: '최대 500자까지 입력 가능합니다.' }]}
        >
          <TextArea rows={3} placeholder="예: 날씨 예보 정보 조회" />
        </Form.Item>

        <Form.Item
          label="카테고리"
          name="category"
          rules={[{ required: true, message: '카테고리를 선택해주세요.' }]}
        >
          <Select placeholder="카테고리 선택">
            {CATEGORY_OPTIONS.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="베이스 URL"
          name="endpointUrl"
          rules={[
            { type: 'url', message: '유효한 URL을 입력해주세요.' },
            { max: 500, message: '최대 500자까지 입력 가능합니다.' },
          ]}
        >
          <Input placeholder="https://api.example.com" />
        </Form.Item>

        <Form.Item
          label="홈페이지 URL"
          name="homepage"
          rules={[
            { type: 'url', message: '유효한 URL을 입력해주세요.' },
            { max: 500, message: '최대 500자까지 입력 가능합니다.' },
          ]}
        >
          <Input placeholder="https://example.com" />
        </Form.Item>

        <Form.Item
          label="API 키 (선택사항)"
          name="apiKey"
          extra="API 키는 암호화되어 저장됩니다."
        >
          <Input.Password placeholder="API 키를 입력하세요" />
        </Form.Item>

        <Form.Item label="활성화" name="isActive" valuePropName="checked">
          <Switch checkedChildren="활성" unCheckedChildren="비활성" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ApiConfigurationModal;
